import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import Modal from '../../../components/Modal';
import { Select, Button } from '../../../components';
import { toast } from 'react-hot-toast';
import { todayKey } from '../../../utils/datetime';
import { LEAVE_TYPES, leaveTypeByKey, resolveLeaveType } from '../../../utils/leaveTypes';

// Book (or edit) a carer's time away (whole day). Staged as a draft change via
// `onStage`; the schedule then shows a grey "deactivated" band and blocks
// assigning them that day. When opened with `editAway` it prefills the booking
// and offers a Delete action (via `onDelete`).
// Same three types, same words, as a leave request and its approval — see
// utils/leaveTypes.js. Booking time away here IS approved leave.
const REASONS = LEAVE_TYPES;

const TimeAwayModal = ({ open, carers = [], defaultDate, editAway = null, onStage, onDelete, onAddAvailability, onClose }) => {
  const isEdit = !!editAway;
  const [employeeId, setEmployeeId] = useState(editAway?.employee_id ? String(editAway.employee_id) : '');
  // New leave defaults to the current date; editing keeps the booking's date.
  const [date, setDate] = useState(editAway?.date || todayKey() || defaultDate);
  // An existing booking may carry a canonical type, a stored label, or one of
  // the old roster words ("Holiday") — all resolve back to a type.
  const [reason, setReason] = useState(
    resolveLeaveType(editAway?.leave_type || editAway?.reason) || 'annual',
  );
  const [note, setNote] = useState(editAway?.notes || '');

  const activeReason = leaveTypeByKey(reason);

  const save = () => {
    if (!employeeId) { toast.error('Pick a carer'); return; }
    if (reason === 'other' && !note.trim()) { toast.error('Please add a note for “Other”'); return; }
    const name = carers.find((c) => String(c.id) === String(employeeId))?.name || editAway?.name || 'Carer';
    onStage({
      employee_id: Number(employeeId),
      name,
      // `leave_type` is the canonical value the leave module stores; `reason`
      // is its label, so the booking reads the same wherever it is shown.
      leave_type: reason,
      reason: activeReason?.label || 'Other',
      notes: note,
      existingId: editAway?.existingId || null,
      date,
    });
    onClose();
  };

  const remove = () => {
    if (!window.confirm('Delete this time away? The carer’s availability for the day will be restored.')) return;
    onDelete({ existingId: editAway?.existingId || null, employee_id: editAway?.employee_id, name: editAway?.name });
    toast.success('Time away removal added to draft');
    onClose();
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={isEdit ? 'Edit time away' : 'Book time away'}
      maxWidth="max-w-xl"
      footer={
        <div className="flex items-center justify-between gap-2">
          {isEdit ? (
            <div className="flex items-center gap-2">
              <Button variant="danger-soft" leftIcon={<Trash2 className="h-4 w-4" />} onClick={remove}>
                Delete leave
              </Button>
              {onAddAvailability && (
                <button
                  onClick={() => onAddAvailability(editAway?.employee_id || (employeeId ? Number(employeeId) : null))}
                  className="inline-flex items-center gap-2 h-10 rounded-lg border border-emerald-200 dark:border-emerald-800/60 px-4 text-sm font-semibold text-emerald-600 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                >
                  + Add availability
                </button>
              )}
            </div>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={save}>
              {isEdit ? 'Save changes' : 'Save time away'}
            </Button>
          </div>
        </div>
      }
    >
      <p className="-mt-2 mb-4 text-xs text-gray-400 dark:text-gray-500">
        {isEdit
          ? 'Update or delete this time-away booking. Deleting restores the carer’s saved availability.'
          : 'Removes availability for the day and returns any overlapping visits to Unassigned.'}
      </p>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Carer</label>
          <Select
            className="w-full"
            value={employeeId}
            onChange={setEmployeeId}
            disabled={isEdit}
            placeholder="Select carer…"
            options={carers.map((c) => ({ value: String(c.id), label: c.name }))}
          />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} disabled={isEdit}
            className="h-11 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#31373f] dark:text-gray-200 px-3 text-sm focus:border-blue-400 focus:outline-none disabled:bg-gray-50 disabled:text-gray-500" />
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 rounded-lg bg-gray-50 dark:bg-[#20242c] px-3 py-2.5 text-[12px] font-semibold text-gray-500 dark:text-gray-400">
        <span className="grid h-4 w-4 place-items-center rounded bg-[#2563EB] text-[10px] text-white">✓</span>
        Whole day
      </div>

      <div className="mt-4">
        <div className="mb-2 text-center text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">Reason</div>
        <div className="grid grid-cols-3 gap-3">
          {REASONS.map((r) => {
            const active = reason === r.key;
            return (
              <button key={r.key} onClick={() => setReason(r.key)}
                className={`rounded-xl border-2 px-3 py-3 text-left transition ${active ? 'border-blue-500 bg-blue-50/40 dark:bg-blue-900/20 dark:border-blue-600' : 'border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-[#31373f]'}`}>
                <div className="text-sm font-bold text-gray-800 dark:text-gray-200">{r.label}</div>
                <div className="mt-0.5 text-[10px] text-gray-400 dark:text-gray-500">{r.sub}</div>
              </button>
            );
          })}
        </div>
        <p className="mt-3 rounded-lg bg-gray-50 dark:bg-[#20242c] px-3 py-2 text-[11px] text-gray-400 dark:text-gray-500">{activeReason?.help}</p>
      </div>

      <div className="mt-4">
        <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Manager note</label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
          placeholder="e.g. Reported sick; cover may be required."
          className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#31373f] dark:text-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" />
      </div>
    </Modal>
  );
};

export default TimeAwayModal;
